import React, { useState, useEffect, useRef } from 'react';

// The mailing-list signup, shared by the section on the home page and the
// standalone kiosk page. `size="large"` gives the touch-friendly variant.
function SignupForm({ size = 'normal', onSubscribed, resetAfterMs = 0 }) {
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '' });
  const [status, setStatus] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const firstFieldRef = useRef(null);

  const large = size === 'large';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Kiosk mode clears itself after a success so the next person gets a fresh
  // form without anyone having to touch the iPad.
  useEffect(() => {
    if (status !== 'success' || !resetAfterMs) return;

    const timer = setTimeout(() => {
      setStatus('');
      setMessage('');
      if (firstFieldRef.current) firstFieldRef.current.focus();
    }, resetAfterMs);

    return () => clearTimeout(timer);
  }, [status, resetAfterMs]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus('');
    setMessage('');

    try {
      const response = await fetch('/api/mailchimp/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus('success');
        setMessage(data.message || 'Thank you for subscribing!');
        setFormData({ firstName: '', lastName: '', email: '' });
        if (onSubscribed) onSubscribed();
      } else {
        setStatus('error');
        setMessage(data.error || 'Failed to subscribe. Please try again.');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      setStatus('error');
      setMessage('Failed to subscribe. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const labelClass = large
    ? 'block text-2xl font-medium text-wine mb-3'
    : 'block text-sm font-medium text-gray-700 mb-2';

  // 16px minimum on the input font size, or iOS Safari zooms in on focus.
  const inputClass = large
    ? 'w-full px-6 py-5 text-2xl border-2 border-gray-300 rounded-xl focus:border-transparent transition'
    : 'w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-transparent transition';

  const fields = [
    { id: 'firstName', label: 'First Name', type: 'text', placeholder: 'Enter your first name', autoComplete: 'given-name' },
    { id: 'lastName', label: 'Last Name', type: 'text', placeholder: 'Enter your last name', autoComplete: 'family-name' },
    { id: 'email', label: 'Email Address', type: 'email', placeholder: 'your.email@example.com', autoComplete: 'email' },
  ];

  return (
    <form onSubmit={handleSubmit} className={large ? 'space-y-8' : 'space-y-6'}>
      {fields.map((field, index) => (
        <div key={field.id}>
          <label htmlFor={field.id} className={labelClass}>
            {field.label} <span aria-hidden="true">*</span>
          </label>
          <input
            ref={index === 0 ? firstFieldRef : undefined}
            type={field.type}
            id={field.id}
            name={field.id}
            value={formData[field.id]}
            onChange={handleChange}
            required
            autoComplete={field.autoComplete}
            placeholder={field.placeholder}
            style={{ fontSize: large ? '1.5rem' : '1rem' }}
            className={inputClass}
          />
        </div>
      ))}

      {/* Announced to screen readers, and to anyone watching the kiosk. */}
      <div role="status" aria-live="polite">
        {status && (
          <div
            className={`rounded-lg ${large ? 'p-6 text-2xl' : 'p-4'} ${
              status === 'success'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}
          >
            <p className="font-medium">{message}</p>
          </div>
        )}
      </div>

      <button
        type="submit"
        disabled={loading}
        className={`w-full text-white font-semibold rounded-lg transition-all ${
          large ? 'py-8 px-6 text-3xl rounded-xl' : 'py-4 px-6'
        } ${loading ? 'cursor-not-allowed' : 'hover:bg-wine-light'}`}
        style={{ backgroundColor: loading ? '#9ca3af' : '#722f37', border: '2px solid #f4bc57' }}
      >
        {loading ? 'Subscribing...' : 'Subscribe'}
      </button>
    </form>
  );
}

export default SignupForm;
